// The hub's search, run in the browser over the search index: heading titles
// and body text, case-insensitively. A body hit carries which occurrence of the
// query it is, counted the way the reader walks the rendered body
// (findInGuide.ts), so a result lands on exactly that match.

import type { IHelpSearchEntry } from '@apis/help';

const SNIPPET_BEFORE = 70;
const SNIPPET_AFTER = 90;
const MAX_BODY_HITS_PER_GUIDE = 2;
const MAX_HITS = 30;

export interface IHelpSearchHit {
  slug: string;
  /** The section the match is in (or, for a heading match, the heading itself); null before the first heading. */
  headingId: string | null;
  headingText: string | null;
  /** Set when the match is in the body text. */
  snippet: string | null;
  /** For a body match: which occurrence of the query in the guide's text (0-based). Null for a heading. */
  occurrence: number | null;
}

/** How many times `query` occurs in `text` before index `at` (non-overlapping, as the reader counts). */
function countBefore(text: string, query: string, at: number): number {
  let count = 0;
  for (let i = text.indexOf(query); i < at; i = text.indexOf(query, i + query.length)) count++;
  return count;
}

export function searchGuides(index: readonly IHelpSearchEntry[], query: string): IHelpSearchHit[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [];
  const hits: IHelpSearchHit[] = [];
  for (const guide of index) {
    for (const heading of guide.headings) {
      if (heading.text.toLowerCase().includes(needle)) {
        hits.push({ slug: guide.slug, headingId: heading.id, headingText: heading.text, snippet: null, occurrence: null });
      }
    }
    const lower = guide.text.toLowerCase();
    // The next hit starts past this one's snippet, so two hits never show the same passage.
    for (let at = lower.indexOf(needle), count = 0; at >= 0 && count < MAX_BODY_HITS_PER_GUIDE; at = lower.indexOf(needle, at + SNIPPET_AFTER + needle.length), count++) {
      const start = Math.max(0, at - SNIPPET_BEFORE);
      const end = Math.min(guide.text.length, at + needle.length + SNIPPET_AFTER);
      const section = guide.headings.findLast((heading) => heading.offset <= at);
      hits.push({
        slug: guide.slug,
        headingId: section ? section.id : null,
        headingText: section ? section.text : null,
        snippet: `${start > 0 ? '…' : ''}${guide.text.slice(start, end)}${end < guide.text.length ? '…' : ''}`,
        occurrence: countBefore(lower, needle, at),
      });
    }
  }
  return hits.slice(0, MAX_HITS);
}
